// utils/commentUtils.ts

import {
  apiCreateComment,
  apiGetAPostCommentReply,
  apiLikeComments,
  apiPostReply,
} from "../api/comment";
import { MetaDataType } from "../interface/search";
import { AccountProfileDataStore } from "../store/contentSlice";

// First, let's define our types that will be used across components
export type Comment = {
  id: number;
  parent_id: number | null;
  post_id: number;
  author_id: number;
  author_name: string;
  author_photo_url: string;
  comment: string;
  likes: number;
  commented_at: string;
  total_replies: number;
  replies?: Comment[];
};

// This interface defines the shape of our comment management functions
export interface CommentManager {
  comments: Comment[];
  addComment: (newComment: Partial<Comment>) => void;
  addReply: (parentId: number, replyContent: string) => void;
  updateComment: (commentId: number, updateData: Partial<Comment>) => void;
  toggleLike: (commentId: number) => void;
  loadReplies: (commentId: number) => Promise<void>;
}

// This is our main utility function that creates a comment manager
export function createCommentManager(
  initialComments: Comment[] = [],
  postId: number,
  currentUser: { id: number; name: string; photo_url: string },
  onStateChange?: (comments: Comment[]) => void
): CommentManager {
  // Create a closure over our comment state
  let comments = [...initialComments];

  // Helper function to update comments and notify listeners
  const updateComments = (newComments: Comment[]) => {
    comments = newComments;
    onStateChange?.(comments);
    return comments;
  };

  // Helper function to recursively update comments
  const updateCommentRecursively = (
    comments: Comment[],
    commentId: number,
    updateFn: (comment: Comment) => Comment
  ): Comment[] => {
    return comments.map((comment) => {
      if (comment.id === commentId) {
        return updateFn(comment);
      }
      if (comment.replies) {
        return {
          ...comment,
          replies: updateCommentRecursively(
            comment.replies,
            commentId,
            updateFn
          ),
        };
      }
      return comment;
    });
  };

  // Create our comment management functions
  const manager: CommentManager = {
    // Getter for current comments
    get comments() {
      return comments;
    },

    // Add a new top-level comment
    addComment(newComment: Partial<Comment>) {
      const comment: Comment = {
        id: Date.now(), // In real implementation, this would come from server
        parent_id: null,
        post_id: postId,
        author_id: currentUser.id,
        author_name: currentUser.name,
        author_photo_url: currentUser.photo_url,
        comment: newComment.comment || "",
        likes: 0,
        commented_at: new Date().toISOString().replace("T", " ").split(".")[0],
        total_replies: 0,
        ...newComment,
      };

      updateComments([...comments, comment]);
    },

    // Add a reply to an existing comment
    addReply(parentId: number, replyContent: string) {
      const reply: Comment = {
        id: Date.now(),
        parent_id: parentId,
        post_id: postId,
        author_id: currentUser.id,
        author_name: currentUser.name,
        author_photo_url: currentUser.photo_url,
        comment: replyContent,
        likes: 0,
        commented_at: new Date().toISOString().replace("T", " ").split(".")[0],
        total_replies: 0,
      };

      const newComments = updateCommentRecursively(
        comments,
        parentId,
        (comment) => ({
          ...comment,
          total_replies: comment.total_replies + 1,
          replies: [...(comment.replies || []), reply],
        })
      );

      updateComments(newComments);
    },

    // Update an existing comment
    updateComment(commentId: number, updateData: Partial<Comment>) {
      const newComments = updateCommentRecursively(
        comments,
        commentId,
        (comment) => ({
          ...comment,
          ...updateData,
        })
      );

      updateComments(newComments);
    },

    // Toggle like on a comment
    toggleLike(commentId: number) {
      const newComments = updateCommentRecursively(
        comments,
        commentId,
        (comment) => ({
          ...comment,
          likes: comment.likes + 1, // In real implementation, this would toggle
        })
      );

      updateComments(newComments);
    },

    // Load replies for a comment (simulated)
    async loadReplies(commentId: number) {
      // In real implementation, this would fetch from server
      await new Promise((resolve) => setTimeout(resolve, 500));
    },
  };

  return manager;
}

// delete the above code and their uses

export const apiAddComment = async (
  comment: string,
  post_id: number,
  account_profile: AccountProfileDataStore
) => {
  try {
    const response = await apiCreateComment({
      post_id: post_id,
      comment: comment,
    });

    // if (response.status === "error") {
    //   console.error("Error creating comment");
    // }

    const newComment: { comment_id: number } = await response.data;
    // Ensure the response matches the CommentType structure
    return {
      id: Number(newComment.comment_id) ?? Math.floor(Math.random() * 100),
      parent_id: null,
      post_id: post_id,
      author_id: account_profile.id,
      author_name: account_profile.name,
      author_photo_url: account_profile.image,
      comment: comment,
      likes: 0,
      commented_at: new Date().toISOString(),
      total_replies: 0,
      replies: [],
    };
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

export const apiAddReply = async (
  parent_id: number,
  reply: string,
  post_id: number,
  account_profile: AccountProfileDataStore
) => {
  try {
    const response = await apiPostReply({
      post_id: post_id,
      comment: reply,
      comment_id: parent_id,
    });

    if (response.status === "error") {
      console.error("Error creating reply");
    }

    const newReply: { comment_id: number } = await response.data;
    
    // Ensure the response matches the CommentType structure
    return {
      id: Number(newReply.comment_id),
      parent_id: parent_id,
      post_id: post_id,
      author_id: account_profile.id,
      author_name: account_profile.name,
      author_photo_url: account_profile.image,
      comment: reply,
      likes: 0,
      commented_at: new Date().toISOString(),
      total_replies: 0,
      replies: [],
    };
  } catch (error) {
    console.error("Error adding reply:", error);
    throw error;
  }
};

export const apiGetCommentReply = async (commentId: number, postId: number) => {
  try {
    const response = await apiGetAPostCommentReply({
      post_id: postId,
      comment_id: commentId,
    });

    if (response.status === "error") {
      console.error("Error fetching comment reply");
    }

    const newReply: { data: any[]; meta: MetaDataType } = await response.data;

    // Ensure the response matches the CommentType structure
    return newReply.data;
  } catch (error) {
    console.error("Error fetching reply:", error);
    throw error;
  }
};

export const apiLikeCommentReply = async (comment_id: number) => {
  try {
    const response = await apiLikeComments(comment_id);

    if (response.status === "error") {
      console.error("Error creating reply");
      return { message: "error" };
    }

    const result: { message: string } = await response.data;

    // Ensure the response matches the CommentType structure
    return result;
  } catch (error) {
    console.error("Error adding reply:", error);
    return { message: "error" };
  }
};
